import React, { useMemo, useState } from "react";
import { TrashIcon } from "../../../components/icons";
import { JSONContent } from "@tiptap/react";
import { ControlledAutocomplete } from "../../../components/forms/AutocompleteSearch";
import { ButtonGroup } from "../../../components/forms/ButtonGroup";
import FormSection, { FieldError } from "../../../components/forms/FormSection";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Badge, Spinner } from "../../../components/ui/feedback";
import { Switch } from "../../../components/ui/switch";
import { MentionEditor } from "./mention-editor";
import ConditionsEditor, { EditableCondition } from "./ConditionsEditor";
import TemplateColumnsEditor from "./TemplateColumnsEditor";
import { useAssemblyContext } from "../context/AssemblyContext";
import { useGetGroupsQuery, useGetGroupPromptsQuery } from "../api/playbookApi";
import { FlowGroupType, GroupModel } from "../types/group-model";
import { PromptModel, PromptDataType } from "../types/prompt-model";
import { AssemblyType } from "../types/assembly-model";
import {
  AssemblyReferenceModel,
  AssemblyReferenceWriteModel,
  EMultiDocumentListFormat,
  ESingleDocumentListFormat,
  ESingleDocumentReferenceFormat,
} from "../types/assembly-reference";
import { AssemblyReferenceType } from "../types/assembly-condition";
import { AssemblyReferenceTemplateColumnWrite, isTemplateEmpty } from "../types/assembly-template";
import { collectPromptObjectIdsFromTemplate, PromptMentionSource } from "./mention-editor/types";
import { GetFilterDataTypeString } from "../utils/formatters";

type ReferenceMode = "list-as" | "per-document";

/** The prompt a reference is built on, once chosen: its label, what it asks, and its type. */
const PromptSummary = ({
  label,
  description,
  dataType,
}: {
  label: string;
  description?: string;
  dataType?: PromptDataType;
}) => (
  <div className="rounded-lg border border-border bg-grey-50 px-3 py-2.5">
    <div className="text-subtitle2 text-strong">{label}</div>
    {description && <div className="mt-0.5 text-caption text-muted-foreground">{description}</div>}
    {dataType !== undefined && (
      <Badge className="mt-2 h-5 text-[11px] text-muted-foreground">
        {GetFilterDataTypeString(dataType)}
      </Badge>
    )}
  </div>
);

export interface ReferenceBuilderProps {
  assemblyType: AssemblyType;
  /**
   * Group a per-document assembly belongs to. When set, the group picker is
   * locked to it and only its prompts are referenceable. Ignored for flow-wide.
   */
  assignedGroupObjectIdentifier?: string;
  onSubmit: (model: AssemblyReferenceWriteModel, promptLabel: string) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
  loading: boolean;
  error: boolean;
  existingReference?: AssemblyReferenceModel;
  isEditMode?: boolean;
}

const ReferenceBuilder = ({
  assemblyType,
  assignedGroupObjectIdentifier,
  onSubmit,
  onDelete,
  onClose,
  loading,
  error,
  existingReference,
  isEditMode = false,
}: ReferenceBuilderProps) => {
  const ctx = useAssemblyContext();
  const supportsModeChoice = assemblyType === AssemblyType.DocxFlowWide;

  // Per-document assemblies are pinned to their assigned group: the picker is
  // hidden and prompts come from that group only. Flow-wide assemblies let the
  // user pick any group first.
  const lockedGroupObjectIdentifier = !supportsModeChoice
    ? assignedGroupObjectIdentifier
    : undefined;

  // Prompt selection (add mode only — editing keeps the existing prompt).
  const [selectedGroup, setSelectedGroup] = useState<GroupModel | null>(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<PromptModel | null>(null);
  const [promptSearch, setPromptSearch] = useState("");

  // Configuration state.
  const [multiFormat, setMultiFormat] = useState<EMultiDocumentListFormat | undefined>(
    existingReference?.multiDocumentListFormat
  );
  const [singleFormat, setSingleFormat] = useState<ESingleDocumentReferenceFormat | undefined>(
    existingReference?.singleDocumentReferenceFormat
  );
  const [answerListFormat, setAnswerListFormat] = useState<ESingleDocumentListFormat | undefined>(
    existingReference?.singleDocumentAnswerListFormat
  );
  const [template, setTemplate] = useState<JSONContent | null>(existingReference?.template ?? null);
  const [templateColumns, setTemplateColumns] = useState<AssemblyReferenceTemplateColumnWrite[]>(
    existingReference?.templateColumns.map((c) => ({ name: c.name, template: c.template })) ?? []
  );
  const [conditions, setConditions] = useState<EditableCondition[]>(
    existingReference?.conditions.map((c) => ({
      isYes: c.isYes,
      fuzzyMatch: c.fuzzyMatch,
      minimumDecimalAnswer: c.minimumDecimalAnswer,
      maximumDecimalAnswer: c.maximumDecimalAnswer,
      textAnswer: c.textAnswer,
      allCategoriesMatch: c.allCategoriesMatch,
      decimalAnswer: c.decimalAnswer,
      decimalOperator: c.decimalOperator,
      dateOperator: c.dateOperator,
      startDate: c.startDate,
      endDate: c.endDate,
      dateAnswer: c.dateAnswer,
      // Client-only stable key for the editor list; reuse the existing
      // condition's server id so it stays stable across edits. Never sent.
      _key: c.objectIdentifier,
      targetPromptObjectIdentifier: c.targetPromptObjectIdentifier,
      categoryConditions: c.categoryConditions.map((cc) => ({
        text: cc.text,
        fuzzyMatch: cc.fuzzyMatch,
      })),
    })) ?? []
  );
  const [submitted, setSubmitted] = useState(false);

  // Single-prompt (old) vs multi-prompt (new) List As authoring. Single: one
  // prompt picked from a dropdown, its fields inserted with the legacy "@"
  // mention. Multi: prompts referenced inline with the "@"/"." cascade. Defaults
  // to the old experience; when editing, derive it from the stored reference so
  // the right editor opens (multi if it has "@" mentions or more than one prompt).
  const [singlePrompt, setSinglePrompt] = useState<boolean>(() => {
    if (!isEditMode || !existingReference) return true;
    const templates = [
      existingReference.template,
      ...existingReference.templateColumns.map((c) => c.template),
    ];
    const usesPromptMentions = templates.some(
      (t) => collectPromptObjectIdsFromTemplate(t).length > 0
    );
    return !(usesPromptMentions || existingReference.prompts.length > 1);
  });

  const { data: groups = [], isLoading: groupsLoading } = useGetGroupsQuery(ctx);

  // When locked (per-document), prompts come from the assigned group; when
  // editing, from the group the reference's prompt belongs to (there is no
  // group picker in edit mode); otherwise from whichever group the user picked.
  const effectiveGroupObjectIdentifier =
    lockedGroupObjectIdentifier ??
    (isEditMode ? existingReference?.groupObjectIdentifier : undefined) ??
    selectedGroup?.objectIdentifier ??
    "";

  const { data: prompts = [], isLoading: promptsLoading } = useGetGroupPromptsQuery(
    {
      ctx,
      groupObjectIdentifier: effectiveGroupObjectIdentifier,
      searchText: promptSearch,
    },
    { skip: !effectiveGroupObjectIdentifier }
  );

  // The conditions editor offers every prompt in the selected group as a
  // possible target, so it needs the full, unfiltered list — independent of
  // whatever the user has typed into the prompt-selection search box above.
  const { data: groupPrompts = [] } = useGetGroupPromptsQuery(
    {
      ctx,
      groupObjectIdentifier: effectiveGroupObjectIdentifier,
      searchText: "",
    },
    { skip: !effectiveGroupObjectIdentifier }
  );

  // Every prompt in the group is referenceable from a List As template via
  // "@" — there is no separate prompt picker; the reference draws on whichever
  // prompts the author actually mentions. Typing "." after a "@" prompt offers
  // that prompt's fields, branching on its type.
  const promptMentionSources: PromptMentionSource[] = useMemo(
    () =>
      groupPrompts.map((p) => ({
        objectIdentifier: p.objectIdentifier,
        label: p.label,
        promptDataType: p.promptDataTypeId,
      })),
    [groupPrompts]
  );

  const promptDataType: PromptDataType | undefined =
    selectedPrompt?.promptDataTypeId ?? existingReference?.filter?.promptDataTypeId;

  const promptLabel = selectedPrompt?.label ?? existingReference?.promptLabel ?? "";
  // The reference mode is driven entirely by the group type — there is no
  // user choice. Bundle groups roll every document into a single answer
  // per collection, so "list as" (one row per document) is meaningless:
  // they get per-document only. Every other group type gets "list as" only.
  const isCombinedDocumentsGroup = selectedGroup?.flowGroupType === FlowGroupType.Bundle;
  const effectiveReferenceMode: ReferenceMode = (() => {
    if (!supportsModeChoice) return "per-document";
    // Editing keeps the mode the existing reference was saved with — there is
    // no group selection in edit mode to derive it from.
    if (isEditMode) {
      return existingReference?.multiDocumentListFormat ? "list-as" : "per-document";
    }
    return selectedGroup && !isCombinedDocumentsGroup ? "list-as" : "per-document";
  })();
  const isListAs = effectiveReferenceMode === "list-as";
  const isTable = isListAs && multiFormat === EMultiDocumentListFormat.Table;

  // A List As reference draws on exactly the prompts its template (or, for a
  // table, its column templates) mentions via "@". This is the source of truth
  // for which prompts the reference is linked to — there is no picker.
  const referencedPromptIds: string[] = useMemo(() => {
    const templates = isTable ? templateColumns.map((c) => c.template) : [template];
    const ids = templates.flatMap((t) => collectPromptObjectIdsFromTemplate(t));
    return [...new Set(ids)];
  }, [isTable, template, templateColumns]);

  const validationError = (): string | null => {
    // A List As reference draws on the prompts the author selected (referenced
    // in its template/columns), so it needs a group (to offer those prompts) and
    // at least one prompt. A per-document reference targets a single prompt.
    if (!isEditMode && isListAs && !selectedGroup && !lockedGroupObjectIdentifier)
      return "Select a group.";
    if (isListAs && !multiFormat) return "Choose a reference format.";
    if (!isListAs && !singleFormat) return "Choose a reference format.";
    // Single-prompt List As needs the one prompt picked; multi-prompt needs at
    // least one "@" mention in the template. (Only enforced when adding — an
    // existing legacy reference has neither but still exports correctly.)
    if (!isEditMode && isListAs && singlePrompt && !selectedPrompt) return "Select a prompt.";
    if (!isEditMode && isListAs && !singlePrompt && referencedPromptIds.length === 0)
      return "Reference at least one prompt in the template using “@”.";
    if (!isEditMode && !isListAs && !selectedPrompt) return "Select a prompt.";

    const needsAnswerListFormat =
      !isListAs &&
      singleFormat === ESingleDocumentReferenceFormat.Answer &&
      promptDataType === PromptDataType.ListOfValues;
    if (needsAnswerListFormat && !answerListFormat) return "Choose an answer list format.";

    return null;
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (validationError()) return;

    // Which prompt(s) the reference draws on. Single-prompt List As uses the one
    // picked prompt; multi-prompt uses exactly the prompts its template/columns
    // mention via "@" (recomputed so edits stay in sync). Either way, on edit we
    // fall back to the reference's existing prompts when nothing new is
    // selected/mentioned. A per-document reference targets the single prompt.
    const existingPromptIds = isEditMode
      ? existingReference!.prompts.map((p) => p.objectIdentifier)
      : [];
    const listAsPromptIds = singlePrompt
      ? selectedPrompt
        ? [selectedPrompt.objectIdentifier]
        : existingPromptIds
      : referencedPromptIds.length > 0
        ? referencedPromptIds
        : existingPromptIds;
    const promptObjectIdentifiers = isListAs
      ? listAsPromptIds
      : isEditMode
        ? existingPromptIds
        : selectedPrompt
          ? [selectedPrompt.objectIdentifier]
          : [];

    const model: AssemblyReferenceWriteModel = {
      promptObjectIdentifiers,
      assemblyReferenceType:
        ctx.source === "flow"
          ? isListAs
            ? AssemblyReferenceType.FlowWide
            : AssemblyReferenceType.Document
          : undefined,
      multiDocumentListFormat: isListAs ? multiFormat : undefined,
      singleDocumentReferenceFormat: !isListAs ? singleFormat : undefined,
      singleDocumentAnswerListFormat: !isListAs ? answerListFormat : undefined,
      template: isListAs && !isTable && !isTemplateEmpty(template) ? template : null,
      templateColumns: isTable ? templateColumns : [],
      // Drop the client-only `_key` so it never reaches the server.
      conditions: conditions.map(({ _key, ...rest }) => rest),
    };

    // A List As reference isn't tied to one prompt, so label the doc
    // placeholder generically (the first mentioned prompt, or "Table"/"List").
    const firstReferencedLabel = groupPrompts.find(
      (p) => p.objectIdentifier === referencedPromptIds[0]
    )?.label;
    const referenceLabel = isTable
      ? "Table"
      : isListAs
        ? singlePrompt
          ? (selectedPrompt?.label ?? promptLabel ?? "List")
          : (firstReferencedLabel ?? "List")
        : promptLabel;
    await onSubmit(model, referenceLabel);
  };

  const showAnswerListFormat =
    !isListAs &&
    singleFormat === ESingleDocumentReferenceFormat.Answer &&
    promptDataType === PromptDataType.ListOfValues;

  const formatError = submitted && validationError()?.includes("format") ? validationError() : null;

  return (
    <div className="flex flex-col gap-5">
      {/* Prompt selection — group first, then prompt */}
      {isEditMode ? (
        <FormSection label="Prompt">
          <PromptSummary label={promptLabel || "Reference"} dataType={promptDataType} />
        </FormSection>
      ) : (
        !lockedGroupObjectIdentifier && (
          <FormSection
            label="Group"
            required
            error={submitted && !selectedGroup && isListAs ? "Select a group." : null}
          >
            <ControlledAutocomplete<GroupModel>
              value={selectedGroup}
              onChange={(group) => {
                setSelectedGroup(group);
                setSelectedPrompt(null);
                setPromptSearch("");
              }}
              inputValue={groupSearch}
              onInputChange={setGroupSearch}
              options={groups}
              loading={groupsLoading}
              getOptionLabel={(option) => option?.name ?? ""}
              placeholder="Search groups"
              clearable
            />
          </FormSection>
        )
      )}

      {/* Reference format: the options differ by mode, the section does not */}
      {isListAs ? (
        <FormSection label="Reference format" required error={formatError}>
          <ButtonGroup
            buttons={[
              { id: EMultiDocumentListFormat.BulletedList, label: "Bullets" },
              { id: EMultiDocumentListFormat.NumberedList, label: "Numbered" },
              { id: EMultiDocumentListFormat.Paragraph, label: "Paragraphs" },
              { id: EMultiDocumentListFormat.Table, label: "Table" },
            ]}
            defaultSelected={multiFormat}
            onSelectionChange={(id) => setMultiFormat(id as EMultiDocumentListFormat)}
          />
        </FormSection>
      ) : (
        <FormSection label="Reference format" required error={formatError}>
          <ButtonGroup
            buttons={[
              { id: ESingleDocumentReferenceFormat.Document, label: "Document" },
              { id: ESingleDocumentReferenceFormat.Quote, label: "Quote" },
              { id: ESingleDocumentReferenceFormat.Answer, label: "Answer" },
            ]}
            defaultSelected={singleFormat}
            onSelectionChange={(id) => setSingleFormat(id as ESingleDocumentReferenceFormat)}
          />
        </FormSection>
      )}

      {/* Per-document prompt selection — a single-document reference targets one
          prompt, shown once a format is chosen. (List As references pick many
          prompts below.) */}
      {!isEditMode && !isListAs && !!singleFormat && (
        <FormSection
          label="Prompt"
          required
          error={submitted && !selectedPrompt ? "Select a prompt." : null}
        >
          <ControlledAutocomplete<PromptModel>
            value={selectedPrompt}
            onChange={setSelectedPrompt}
            inputValue={promptSearch}
            onInputChange={setPromptSearch}
            options={prompts}
            loading={promptsLoading}
            getOptionLabel={(option) => option?.label ?? ""}
            placeholder="Search prompts"
            disabled={!effectiveGroupObjectIdentifier}
            clearable
          />
          {selectedPrompt && (
            <PromptSummary
              label={selectedPrompt.label}
              description={selectedPrompt.description}
              dataType={selectedPrompt.promptDataTypeId}
            />
          )}
        </FormSection>
      )}

      {showAnswerListFormat && (
        <FormSection
          label="Answer list format"
          required
          error={submitted && !answerListFormat ? "Choose an answer list format." : null}
        >
          <ButtonGroup
            buttons={[
              { id: ESingleDocumentListFormat.BulletedList, label: "Bullets" },
              { id: ESingleDocumentListFormat.NumberedList, label: "Numbered" },
              { id: ESingleDocumentListFormat.Text, label: "Text" },
            ]}
            defaultSelected={answerListFormat}
            onSelectionChange={(id) => setAnswerListFormat(id as ESingleDocumentListFormat)}
          />
        </FormSection>
      )}

      {/* Single-prompt (old) vs multi-prompt (new) List As authoring. Only
          offered when adding; editing opens whichever mode the reference was
          saved in. */}
      {!isEditMode && isListAs && !!multiFormat && effectiveGroupObjectIdentifier && (
        <FormSection
          label="Prompts"
          hint={
            singlePrompt
              ? "Pick one prompt and use “@” to insert its answer, quote or document name."
              : "Reference several prompts inline with “@” — the reference uses whichever you mention."
          }
        >
          <div className="flex flex-row items-center gap-2 py-1.5">
            <Switch
              id="reference-single-prompt"
              checked={singlePrompt}
              onCheckedChange={setSinglePrompt}
            />
            <label htmlFor="reference-single-prompt" className="text-body2">
              Single prompt reference
            </label>
          </div>
          {singlePrompt && (
            <>
              <ControlledAutocomplete<PromptModel>
                value={selectedPrompt}
                onChange={setSelectedPrompt}
                inputValue={promptSearch}
                onInputChange={setPromptSearch}
                options={prompts}
                loading={promptsLoading}
                getOptionLabel={(option) => option?.label ?? ""}
                placeholder="Search prompts"
                disabled={!effectiveGroupObjectIdentifier}
                clearable
              />
              {submitted && !selectedPrompt && <FieldError>Select a prompt.</FieldError>}
            </>
          )}
        </FormSection>
      )}

      {/* List As templates — single-prompt uses the legacy "@" field mention;
          multi-prompt references prompts inline with "@", then "." for the
          field. The group must be chosen first so "@" has prompts to offer. */}
      {isListAs && multiFormat && effectiveGroupObjectIdentifier && (
        <FormSection
          label={isTable ? "Column templates" : "Template"}
          hint={
            singlePrompt ? (
              isTable ? (
                "For each column, type “@” to insert the selected prompt’s answer, quote or document name."
              ) : (
                "Type “@” to insert the selected prompt’s answer, quote or document name, and mix it with your own wording."
              )
            ) : (
              <>
                {isTable
                  ? "For each column, type “@” to pick a prompt, then “.” to choose what to pull from it"
                  : "Write your text, and type “@” wherever you want to pull in an answer — pick a prompt, then “.” to choose what to pull from it"}
                {" — for example "}
                <span className="font-semibold text-primary">@Agreement Date</span>
                <span className="font-semibold text-primary-dark">.document</span>
                {". You can reference several prompts, and mix them with your own wording."}
              </>
            )
          }
          error={
            !isEditMode && !singlePrompt && submitted && referencedPromptIds.length === 0
              ? "Reference at least one prompt using “@”."
              : null
          }
        >
          {isTable ? (
            <TemplateColumnsEditor
              columns={templateColumns}
              onChange={setTemplateColumns}
              prompts={singlePrompt ? undefined : promptMentionSources}
              singlePrompt={singlePrompt}
              anchorPromptDataType={promptDataType}
            />
          ) : (
            <MentionEditor
              value={template}
              onChange={setTemplate}
              placeholder={
                singlePrompt
                  ? "Type @ to insert the answer, quote or document name…"
                  : "Type @ to insert a prompt, then . to pick its answer, document or quote…"
              }
              prompts={singlePrompt ? undefined : promptMentionSources}
              singlePrompt={singlePrompt}
              anchorPromptDataType={promptDataType}
            />
          )}
        </FormSection>
      )}

      <FormSection label="Conditions">
        <ConditionsEditor
          conditions={conditions}
          onChange={setConditions}
          ownPromptObjectIdentifier={
            (isListAs
              ? singlePrompt
                ? (selectedPrompt?.objectIdentifier ?? existingReference?.promptObjectIdentifier)
                : // Multi-prompt draws on the "@" mentions; a legacy "@document"
                  // template has none, so fall back to the reference's own prompt.
                  (referencedPromptIds[0] ?? existingReference?.promptObjectIdentifier)
              : isEditMode
                ? existingReference?.promptObjectIdentifier
                : selectedPrompt?.objectIdentifier) ?? ""
          }
          groupObjectIdentifier={effectiveGroupObjectIdentifier || undefined}
          groupPrompts={groupPrompts}
          ownPromptDataType={
            isListAs
              ? singlePrompt
                ? (selectedPrompt?.promptDataTypeId ?? promptDataType)
                : // No "@" mentions (legacy "@document" template): fall back to the
                  // reference's own prompt data type rather than reading undefined.
                  referencedPromptIds.length > 0
                  ? (groupPrompts.find((p) => p.objectIdentifier === referencedPromptIds[0])
                      ?.promptDataTypeId ?? promptDataType)
                  : promptDataType
              : isEditMode
                ? promptDataType
                : (selectedPrompt?.promptDataTypeId ?? promptDataType)
          }
        />
      </FormSection>

      {(error || (submitted && validationError())) && (
        <Alert severity="error">
          {submitted && validationError()
            ? validationError()
            : "Error saving reference. Please try again."}
        </Alert>
      )}

      {/* Sticky so the form's decision is always in reach on a long form; bleeds to
          the pane edges so it reads as a footer rather than a card. */}
      <div className="sticky bottom-0 -mx-3.5 flex flex-col gap-2 border-t border-border bg-card px-3.5 py-3">
        <div className="flex flex-row gap-2">
          <Button variant="secondary-outline" size="lg" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button size="lg" disabled={loading} className="flex-1" onClick={handleSubmit}>
            {isEditMode ? "Update" : "Insert reference"}
            {loading && <Spinner size={16} className="text-current" />}
          </Button>
        </div>
        {isEditMode && onDelete && (
          <Button
            variant="ghost"
            size="lg"
            disabled={loading}
            onClick={() => void onDelete()}
            className="text-error hover:bg-error-tint hover:text-error"
          >
            <TrashIcon />
            Delete reference
          </Button>
        )}
      </div>
    </div>
  );
};

export default ReferenceBuilder;
